import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Message, Messages, Panel } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading } from "capitalrx-components-2.0/lib/typography";

import { getFormulary } from "@/api/axios/formulary";
import { useExternalLink, useSelectedEnrollmentPlan } from "@/premium/hooks";
import { ThemedButton } from "@/premium/themedComponents";
import { ANALYTIC_EVENTS, logEvent } from "@/utils/analytics";

const translationPath = "PLAN_DETAILS.formularyPanel";

export const FormularyPanel = () => {
  const [isLoadingFormularyUrl, setIsLoadingFormularyUrl] = useState<boolean>(true);
  const [formularyUrl, setFormularyUrl] = useState<string>(undefined);
  const { t: translate } = useTranslation();
  const openExternalLink = useExternalLink();
  const { enrollmentPlanCsid, overrideFormularyLookupURL } = useSelectedEnrollmentPlan();

  useEffect(() => {
    setIsLoadingFormularyUrl(true);
    if (enrollmentPlanCsid?.id) {
      getFormulary(enrollmentPlanCsid?.id)
        .then((formulary) => {
          setFormularyUrl(formulary.lookup_tool_url);
        })
        .catch(() => {
          setFormularyUrl(undefined);
        })
        .finally(() => {
          setIsLoadingFormularyUrl(false);
        });
    }
  }, [enrollmentPlanCsid]);

  const sendToFormulary = useCallback(() => {
    logEvent(ANALYTIC_EVENTS.VIEW_FORMULARY);
    const url = overrideFormularyLookupURL ?? formularyUrl;
    if (!url) return;
    openExternalLink(url);
  }, [formularyUrl, overrideFormularyLookupURL, openExternalLink]);

  return (
    <Panel>
      <Stack>
        <Heading>{translate(`${translationPath}.lookupFormulary`)}</Heading>
        <Body>{translate(`${translationPath}.formularyInfo`)}</Body>
        <Message visible appearance={Messages.Informational}>
          <Body>{translate(`${translationPath}.drugIncludedMessage`)}</Body>
        </Message>
        <Shelf>
          <ThemedButton icon="link-out" onClick={sendToFormulary} disabled={isLoadingFormularyUrl}>
            {translate(`${translationPath}.viewFormulary`)}
          </ThemedButton>
          <Shelf.Spacer />
        </Shelf>
      </Stack>
    </Panel>
  );
};
